import { useEffect, useState } from "react";
import { fetchTowerDay } from "./useTowerData";
import type { TowerDashboard } from "../../../types/controlTower";

// Día de las pestañas con selector de fecha y su tablero. Sin una fecha elegida
// sigue a "hoy" (también al pasar la medianoche) y usa la lectura que ya tiene
// la Torre; otro día se pide aparte. La respuesta de otro día solo se muestra
// bajo la fecha que se pidió. `retry` vuelve a pedir el día elegido.
export function useTowerDay(today: string, todayData: TowerDashboard, token: string | null) {
  const [picked, setPicked] = useState<string | null>(null);
  const day = picked || today;
  const [other, setOther] = useState<{ day: string; data: TowerDashboard } | null>(null);
  const [failure, setFailure] = useState<{ day: string; message: string } | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (day === today || !token) return;
    const controller = new AbortController();
    setFailure((previous) => (previous?.day === day ? null : previous));
    (async () => {
      try {
        const payload = await fetchTowerDay(day, token, controller.signal);
        if (!controller.signal.aborted) setOther({ day, data: payload });
      } catch (err) {
        if (!controller.signal.aborted)
          setFailure({ day, message: err instanceof Error ? err.message : "No se pudo leer ese día." });
      }
    })();
    return () => controller.abort();
  }, [day, today, token, attempt]);

  // Elegir hoy (o borrar la fecha) vuelve a seguir el día en curso.
  const setDay = (value: string) => setPicked(!value || value === today ? null : value);
  const data = day === today ? todayData : other?.day === day ? other.data : null;
  const error = day !== today && failure?.day === day ? failure.message : "";
  const retry = () => setAttempt((n) => n + 1);
  const pastNote = day === today || !data ? "" : historyNote(data.quality);
  return { day, setDay, data, error, retry, pastNote };
}

// En un día pasado, si los estados son los de ese día (historial), los de ahora
// o una mezcla: los envíos sin historial quedan con su estado actual.
export function historyNote({ stateFromHistory, shipmentsRead }: TowerDashboard["quality"]): string {
  if (stateFromHistory === 0) return "No hay historial de ese día: los estados que se ven son los de ahora.";
  if (stateFromHistory >= shipmentsRead) return "Estados al cierre de ese día, tomados del historial de Lightdata.";
  return `${stateFromHistory} de ${shipmentsRead} envíos con el estado al cierre de ese día; el resto, con el estado de ahora.`;
}
